import * as THREE from "three";
import type { AsteroidBody, AsteroidDef, Biome, Vec2 } from "@dyson/shared";

/**
 * The background life round the rocks: nothing to catch or fight, just there,
 * all made on the client (nothing is sent) for the rocks near the camera
 * (the skyfish round the green rocks are real creatures: see SKYFISH):
 * - jellies drifting slowly round the dim cold rocks, pulsing and glowing;
 * - ember moths fluttering about the scorched rocks near the suns.
 * And bits of leaf (see leaf): dropped by gas bags eating at the trees,
 * shaken down by someone chopping one, and blown off one as it comes down.
 * Behind the play plane, so they pass behind the rocks.
 */

type Kind = "jelly" | "moth" | "leaf";

/** Which (if any) live round a rock, by its biome. */
const LIVES: Partial<Record<Biome, Exclude<Kind, "leaf">>> = {
  twilight: "jelly",
  frost: "jelly",
  dark: "jelly",
  scorched: "moth",
};

/** Jellies' glow, by biome. Brighter than white, so they bloom a little. */
const JELLY: Partial<Record<Biome, [number, number, number]>> = {
  twilight: [1.1, 0.7, 1.5],
  frost: [0.8, 1.2, 1.5],
  dark: [0.5, 1.4, 1.2],
};

/** Within this of the camera, a rock's life is made; past `GONE`, it goes. */
const NEAR = 260;
const GONE = 340;

/** A number 0..1 fixed by a rock's id (and `salt`), so its life is the same each time it's made. */
function hash(id: string, salt: number): number {
  let h = 2166136261 ^ Math.imul(salt + 1, 374761393);
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  h ^= h >>> 13;
  h = Math.imul(h, 1274126177);
  return ((h >>> 0) % 100003) / 100003;
}

function sprite(w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  draw(c.getContext("2d")!);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A jelly: a soft glowing dome with trailing threads (tinted by its colour). */
const jellyTexture = () =>
  sprite(64, 64, (ctx) => {
    const g = ctx.createRadialGradient(32, 24, 2, 32, 24, 22);
    g.addColorStop(0, "rgba(255,255,255,0.95)");
    g.addColorStop(0.6, "rgba(255,255,255,0.45)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(32, 26, 20, 16, 0, Math.PI, 0);
    ctx.quadraticCurveTo(32, 34, 12, 26);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.5)";
    ctx.lineWidth = 1.5;
    for (let i = 0; i < 5; i++) {
      const x = 18 + i * 7;
      ctx.beginPath();
      ctx.moveTo(x, 28);
      ctx.bezierCurveTo(x - 4, 40, x + 4, 50, x - 2, 62);
      ctx.stroke();
    }
  });

/** An ember moth from above-ish: two pairs of glowing wings and a dark body. */
const mothTexture = () =>
  sprite(32, 32, (ctx) => {
    const g = ctx.createRadialGradient(16, 16, 1, 16, 16, 15);
    g.addColorStop(0, "rgba(255,240,200,1)");
    g.addColorStop(1, "rgba(255,140,40,0)");
    ctx.fillStyle = g;
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.ellipse(16 + s * 8, 12, 8, 6, s * 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(16 + s * 6, 21, 5, 4, -s * 0.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = "rgba(60,30,20,1)";
    ctx.fillRect(15, 8, 2, 16);
  });

/** A bit of leaf. */
const leafTexture = () =>
  sprite(16, 16, (ctx) => {
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.ellipse(8, 8, 6, 3, 0.6, 0, Math.PI * 2);
    ctx.fill();
  });

/** One instanced mesh of one kind of sprite: filled afresh each frame. */
class Sprites {
  readonly mesh: THREE.InstancedMesh;
  private n = 0;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly s = new THREE.Vector3();
  private readonly p = new THREE.Vector3();
  private readonly c = new THREE.Color();
  private readonly z = new THREE.Vector3(0, 0, 1);

  constructor(
    private readonly capacity: number,
    texture: THREE.Texture,
    additive: boolean,
  ) {
    this.mesh = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending }),
      capacity,
    );
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
  }

  begin(): void {
    this.n = 0;
  }

  put(x: number, y: number, z: number, angle: number, sx: number, sy: number, r: number, g: number, b: number): void {
    if (this.n >= this.capacity) return;
    this.m.compose(this.p.set(x, y, z), this.q.setFromAxisAngle(this.z, angle), this.s.set(sx, sy, 1));
    this.mesh.setMatrixAt(this.n, this.m);
    this.mesh.setColorAt(this.n, this.c.setRGB(r, g, b));
    this.n++;
  }

  end(): void {
    this.mesh.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}

/** The life round one rock. */
interface Flock {
  kind: Exclude<Kind, "leaf">;
  def: AsteroidDef;
  /** Its members: how far round the rock (radians), how far out past its edge, and each one's own wobble and size. */
  members: { round: number; out: number; phase: number; size: number }[];
}

interface Leaf {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** The way it falls (a unit vector), or none: it floats off, slowing. */
  down: Vec2 | null;
  spin: number;
  age: number;
  life: number;
  size: number;
  color: readonly [number, number, number];
}

/** How a bit of leaf goes (see Skylife.leaf). */
export interface LeafLook {
  down?: Vec2 | null;
  /** Seconds before it's gone. */
  life?: number;
  size?: number;
  color?: readonly [number, number, number];
}

const LEAF_GREEN = [0.35, 0.62, 0.22] as const;
const LEAVES = 320;

export class Skylife {
  readonly group = new THREE.Group();
  private readonly jellies = new Sprites(120, jellyTexture(), true);
  private readonly moths = new Sprites(240, mothTexture(), true);
  private readonly leaves = new Sprites(LEAVES, leafTexture(), false);
  private readonly flocks = new Map<string, Flock>();
  private readonly fallen: Leaf[] = [];
  private time = 0;
  private untilLook = 0;

  constructor() {
    this.group.add(this.jellies.mesh, this.moths.mesh, this.leaves.mesh);
  }

  /** A bit of leaf from (x, y), going (vx, vy): falling `down` (if any) and fluttering, or floating off, slowing and turning over. */
  leaf(x: number, y: number, vx: number, vy: number, look: LeafLook = {}): void {
    if (this.fallen.length >= LEAVES) this.fallen.shift();
    this.fallen.push({ x, y, vx, vy, down: look.down ?? null, spin: Math.random() * 6, age: 0, life: look.life ?? 2.5, size: look.size ?? 0.5, color: look.color ?? LEAF_GREEN });
  }

  /** `camera`: where the camera looks (life is made round the rocks near it); `rockAt`: where each rock is drawn. */
  update(dt: number, camera: Vec2, asteroids: Map<string, AsteroidBody>, rockAt: (def: AsteroidDef) => Vec2): void {
    this.time += dt;
    this.untilLook -= dt;
    if (this.untilLook <= 0) {
      this.untilLook = 0.5;
      this.look(camera, asteroids, rockAt);
    }
    this.jellies.begin();
    this.moths.begin();
    this.leaves.begin();
    for (const [id, f] of this.flocks) {
      if (!asteroids.has(id)) {
        this.flocks.delete(id);
        continue;
      }
      const o = rockAt(f.def);
      const cx = o.x + f.def.w / 2;
      const cy = o.y + f.def.h / 2;
      if (f.kind === "jelly") this.drift(f, cx, cy);
      else this.flutter(f, cx, cy);
    }
    this.fall(dt);
    this.jellies.end();
    this.moths.end();
    this.leaves.end();
  }

  /** Make the life round rocks the camera's come near; forget it round those it's left. */
  private look(camera: Vec2, asteroids: Map<string, AsteroidBody>, rockAt: (def: AsteroidDef) => Vec2): void {
    for (const [id, f] of this.flocks) {
      const o = rockAt(f.def);
      if (Math.hypot(o.x + f.def.w / 2 - camera.x, o.y + f.def.h / 2 - camera.y) > GONE + f.def.radius) this.flocks.delete(id);
    }
    for (const [id, a] of asteroids) {
      if (this.flocks.has(id) || a.def.radius < 6) continue;
      const kind = a.def.biome && LIVES[a.def.biome];
      if (!kind || hash(id, 0) > 0.7) continue;
      const o = rockAt(a.def);
      if (Math.hypot(o.x + a.def.w / 2 - camera.x, o.y + a.def.h / 2 - camera.y) > NEAR + a.def.radius) continue;
      this.flocks.set(id, this.flockFor(id, a.def, kind));
    }
  }

  private flockFor(id: string, def: AsteroidDef, kind: Flock["kind"]): Flock {
    const n = kind === "jelly" ? 2 + Math.floor(hash(id, 1) * 4) : 4 + Math.floor(hash(id, 1) * 6);
    const members = Array.from({ length: n }, (_, i) => ({
      round: hash(id, 10 + i) * Math.PI * 2,
      out: hash(id, 30 + i) * (kind === "jelly" ? 22 : 8),
      phase: hash(id, 50 + i) * Math.PI * 2,
      size: 0.8 + hash(id, 70 + i) * 0.5,
    }));
    return { kind, def, members };
  }

  /** Jellies: drifting slowly round, bobbing out and in, pulsing, glowing; upright away from the rock. */
  private drift(f: Flock, cx: number, cy: number): void {
    const [r, g, b] = JELLY[f.def.biome!] ?? [1, 1, 1.3];
    for (const m of f.members) {
      const a = m.round + this.time * 0.01 * (m.phase > Math.PI ? 1 : -1);
      const d = f.def.radius + 8 + m.out + Math.sin(this.time * 0.4 + m.phase) * 2.5;
      const pulse = Math.sin(this.time * 1.8 + m.phase);
      const s = 2.4 * m.size;
      const glow = 0.75 + 0.25 * pulse;
      this.jellies.put(cx + Math.cos(a) * d, cy + Math.sin(a) * d, -1.6, a - Math.PI / 2, s * (1 - 0.08 * pulse), s * (1 + 0.12 * pulse), r * glow, g * glow, b * glow);
    }
  }

  /** Ember moths: each fluttering about its own spot just off the rock, wings beating. */
  private flutter(f: Flock, cx: number, cy: number): void {
    for (const [i, m] of f.members.entries()) {
      const t = this.time + m.phase * 3;
      const d = f.def.radius + 3 + m.out + Math.sin(t * 0.8) * 2;
      const a = m.round + Math.sin(t * 0.37 + i) * 0.08;
      const x = cx + Math.cos(a) * d + Math.sin(t * 1.7) * 2.2 + Math.sin(t * 3.1) * 0.8;
      const y = cy + Math.sin(a) * d + Math.cos(t * 1.3) * 2.2 + Math.sin(t * 2.7) * 0.8;
      const flap = 0.35 + 0.65 * Math.abs(Math.sin(t * 16));
      const s = 1.1 * m.size;
      this.moths.put(x, y, -0.8, Math.sin(t * 1.1) * 0.5, s * flap, s, 1.6, 0.85, 0.35);
    }
  }

  private fall(dt: number): void {
    let n = 0;
    const drag = Math.exp(-dt * 1.2);
    for (const l of this.fallen) {
      l.age += dt;
      if (l.age > l.life) continue;
      l.vx *= drag;
      l.vy *= drag;
      if (l.down) {
        l.vx += l.down.x * 2 * dt;
        l.vy += l.down.y * 2 * dt;
      }
      // Fluttering side to side across the way it falls.
      const flutter = Math.sin(l.age * 4 + l.spin) * 0.8;
      const side = l.down ? { x: -l.down.y, y: l.down.x } : { x: 0, y: 0 };
      l.x += (l.vx + side.x * flutter) * dt;
      l.y += (l.vy + side.y * flutter) * dt;
      this.fallen[n++] = l;
      const s = l.size * Math.min(1, (l.life - l.age) * 2, l.age * 8);
      // Turning over as it goes: wider and narrower.
      const turn = 0.35 + 0.65 * Math.abs(Math.cos(l.age * 2.5 + l.spin));
      this.leaves.put(l.x, l.y, 0.55, l.spin + l.age * 2, s, s * turn, l.color[0], l.color[1], l.color[2]);
    }
    this.fallen.length = n;
  }
}
